// M2 prorationing & hot oil (M2_SPEC §7, DESIGN §5.5).
// A prorated well produces min(potential, allowable); the rest stays in the reservoir and the decline clock runs at
// the produced rate. "Running hot" produces part or all of the excess anyway: hot barrels go straight to bootleg
// buyers at HOT_OIL.priceMult × the local price (no transport, no severance), and every month a field ran hot the
// Railroad Commission (later the federal tender board) may catch it.
// Everything here is inert before 1901 (no proration group applies before 1927) and draws no RNG unless a field ran hot.
import { wellsOnField } from '../indexes.js';
import { chance } from '../../core/rng.js';
import { dateToDay, dayToDate } from '../../core/time.js';
import { PRORATION, HOT_OIL, STRIPPER_BBLD, allowable as ruleAllowable, lerpAt } from '../../data/m2/rules.js';
import { scriptedFlag } from '../../data/news.js';
import { spotPrice, netPriceMult } from '../../economy/market.js';
import { fieldDef, eraDollars, yf } from '../defs.js';
import { clamp, notify, spend, earn, fmt$ } from '../util.js';
import { royaltyRate } from '../production.js';

// East Texas martial law (Gov. Sterling, 17 Aug 1931): the National Guard shut the whole field in until the
// Railroad Commission's first 225 bbl/d allowable on 5 Sept 1931. Busts are 3× likelier while troops patrol
// (until the federal court ruling of Feb 1932). Oklahoma's martial law is the `martialLawOK` flag (Aug 1931).
export const EAST_TEXAS_SHUTDOWN = { from: dateToDay(1931, 8, 17), until: dateToDay(1931, 9, 5) };
export const TEXAS_MARTIAL_LAW = { from: dateToDay(1931, 8, 17), until: dateToDay(1932, 2, 25) };
export const MARTIAL_LAW_BUST_MULT = 3;
export const HOT_LOG_DAYS = 90;
// Bootleg buyers (night trucks, tea-pot refineries) only take so much: a hot well runs at most this many times its
// allowable on top of the allowable itself.
export const HOT_MAX_OVER_ALLOWABLE = 2;

// Flag value: on if the live state flag is set or scripted history says so on this day. (newGame starts some flags
// at false, and tests may jump days without ticking news; both sources come from the same SCRIPTED items.)
export function flagOn(state, flag) {
  return state.flags?.[flag] === true || !!scriptedFlag(flag, state.day);
}

function flagsFor(state, group) {
  const g = PRORATION[group];
  return g?.flag ? { [g.flag]: flagOn(state, g.flag) } : {};
}

export const groupOf = (fieldId) => fieldDef(fieldId)?.prorationGroup ?? null;

// Is any proration schedule in force for this field today?
export function prorationActive(state, fieldId) {
  const group = groupOf(fieldId);
  const g = PRORATION[group];
  if (!g) return false;
  if (group === 'east_texas' && state.day >= EAST_TEXAS_SHUTDOWN.from && state.day < EAST_TEXAS_SHUTDOWN.until) return true;
  return yf(state) >= g.from && (!g.flag || flagOn(state, g.flag));
}

// Allowable bbl/d for a well with natural potential `potential` on this field today.
export function allowableFor(state, fieldId, potential) {
  const group = groupOf(fieldId);
  if (!group || !(potential > 0)) return potential;
  if (group === 'east_texas' && state.day >= EAST_TEXAS_SHUTDOWN.from && state.day < EAST_TEXAS_SHUTDOWN.until) {
    return potential <= STRIPPER_BBLD ? potential : 0;
  }
  return ruleAllowable(group, yf(state), potential, flagsFor(state, group));
}

// allowableFor(state, fieldId, ·) with today's per-field terms resolved once (perf: survey.tickRivals calls it for every
// rival well every day). Same arithmetic as allowableFor/rules.allowable, so results are bit-identical.
export function allowableFn(state, fieldId) {
  const group = groupOf(fieldId);
  if (!group) return (potential) => potential;
  if (group === 'east_texas' && state.day >= EAST_TEXAS_SHUTDOWN.from && state.day < EAST_TEXAS_SHUTDOWN.until) {
    return (potential) => (!(potential > 0) ? potential : potential <= STRIPPER_BBLD ? potential : 0);
  }
  const g = PRORATION[group], y = yf(state);
  if (!g || y < g.from || (g.flag && !flagOn(state, g.flag))) return (potential) => potential;
  const perWell = g.perWell ? lerpAt(g.perWell, y) : null;
  const frac = g.perWell ? null : lerpAt(g.fractionOfPotential, y);
  return (potential) => {
    if (!(potential > 0) || potential <= STRIPPER_BBLD) return potential;
    const cap = perWell != null ? perWell : potential * frac;
    return Math.max(Math.min(STRIPPER_BBLD, potential), Math.min(potential, cap));
  };
}

export function lawText(state, fieldId) {
  const group = groupOf(fieldId);
  const g = PRORATION[group];
  if (!g) return 'No prorationing: produce wide open (rule of capture).';
  const y = yf(state);
  if (group === 'east_texas' && state.day >= EAST_TEXAS_SHUTDOWN.from && state.day < EAST_TEXAS_SHUTDOWN.until) return 'Martial law: the National Guard has shut in every flush well in East Texas.';
  if (!prorationActive(state, fieldId)) return y < g.from ? `No prorationing yet (${group.replace('_', ' ')} schedule from ${Math.floor(g.from)}).` : 'No prorationing order in force.';
  const fed = flagOn(state, 'connallyAct') ? ' Interstate shipment of hot oil is a federal crime (Connally Act).' : flagOn(state, 'hotOilFederal') ? ' Federal tender board polices hot oil (NIRA §9c).' : '';
  if (g.perWell) return `Railroad Commission allowable: ${Math.round(lerpAt(g.perWell, y))} bbl/d per well.${fed}`;
  const pct = Math.round(100 * lerpAt(g.fractionOfPotential, y));
  return `${g.voluntary ? 'Voluntary curtailment' : group === 'oklahoma' ? 'Corporation Commission proration' : group === 'yates' ? 'Yates operators’ agreement' : 'Railroad Commission proration'}: ${pct}% of potential.${fed}`;
}

// ---- production hook ----------------------------------------------------------------------
// Called by production.js for every flowing well with its natural potential q (bbl/d). Returns
// { q: legal bbl to store, hot: bbl sold hot (already sold and removed from the reservoir), age: decline-clock step }.
const PASS = Object.freeze({ q: 0, hot: 0, age: 1 });
export function prorate(state, well, potential, res) {
  const p = well.prod;
  if (!(potential > 0) || !groupOf(well.fieldId) || yf(state) < 1901) {
    if (p && p.allowable != null) { p.allowable = null; p.potential = null; p.hotToday = 0; }
    return { ...PASS, q: potential };
  }
  const allow = allowableFor(state, well.fieldId, potential);
  p.potential = potential;
  p.allowable = allow;
  p.hotToday = 0;
  if (allow >= potential - 1e-9) return { ...PASS, q: potential };
  const hotShare = clamp(p.hot ?? 0, 0, 1);
  let hot = hotShare > 0 ? Math.min((potential - allow) * hotShare, Math.max(allow, STRIPPER_BBLD) * HOT_MAX_OVER_ALLOWABLE) : 0;
  if (res) hot = Math.min(hot, Math.max(0, res.remaining - allow));
  if (hot > 1e-6) sellHot(state, well, hot, res);
  const produced = allow + hot;
  return { q: allow, hot, age: clamp(produced / potential, 0, 1) };
}

export function hotPrice(state, fieldId) {
  return Math.max(0, spotPrice(state)) * netPriceMult(state, fieldId, null) * HOT_OIL.priceMult;
}

function sellHot(state, well, bbl, res) {
  if (res) res.remaining = Math.max(0, res.remaining - bbl);
  const p = well.prod;
  p.hotToday = bbl;
  p.cum += bbl;
  const price = hotPrice(state, well.fieldId);
  const gross = bbl * price;
  earn(state, gross, 'revenue');
  // landowners still get their royalty (hot-oil runners paid it to keep the lease); no severance, no tariff
  if (gross > 0) spend(state, gross * royaltyRate(state, well.fieldId), 'royalties', false);
  const st = state.company.stats;
  st.hotOilBbl = (st.hotOilBbl ?? 0) + bbl;
  st.bblProduced += bbl; st.bblSold += bbl; st.revenueTotal += gross;
  const h = hotState(state, well.fieldId);
  h.monthBbl += bbl;
  const last = h.log[h.log.length - 1];
  if (last && last[0] === state.day) last[1] += bbl; else h.log.push([state.day, bbl]);
  while (h.log.length && h.log[0][0] < state.day - HOT_LOG_DAYS) h.log.shift();
}

export function hotState(state, fieldId) {
  const f = state.fields[fieldId];
  f.hot ??= { mult: 0, monthBbl: 0, log: [], bustUntil: 0 };
  return f.hot;
}

// ---- monthly bust roll ----------------------------------------------------------------------
export function federalLaw(state) { return flagOn(state, 'hotOilFederal') || flagOn(state, 'connallyAct'); }

export function martialLaw(state, fieldId) {
  const rid = fieldDef(fieldId)?.regionId;
  if (rid === 'oklahoma' && flagOn(state, 'martialLawOK') && yf(state) < 1933) return true;
  return rid === 'texas' && state.day >= TEXAS_MARTIAL_LAW.from && state.day < TEXAS_MARTIAL_LAW.until;
}

export function bustChance(state, fieldId) {
  const base = federalLaw(state) ? HOT_OIL.bustPerMonthFederal : HOT_OIL.bustPerMonth;
  return Math.min(0.95, base * (martialLaw(state, fieldId) ? MARTIAL_LAW_BUST_MULT : 1));
}

export function hotBbl90(state, fieldId) {
  const h = state.fields[fieldId]?.hot;
  if (!h) return 0;
  return h.log.filter(([d]) => d >= state.day - HOT_LOG_DAYS).reduce((a, [, b]) => a + b, 0);
}

export function bustFine(state, fieldId) {
  return eraDollars(state, HOT_OIL.finePerBbl, HOT_OIL.baseYear) * hotBbl90(state, fieldId) * (federalLaw(state) ? HOT_OIL.fineMultFederal : 1);
}

// Called on the 1st of each month (after the month rolled).
export function tickProrationMonthly(state) {
  for (const f of Object.values(state.fields)) {
    const h = f.hot;
    if (!h || !(h.monthBbl > 0)) continue;
    const p = bustChance(state, f.id);
    if (chance(state, p)) bust(state, f.id);
    h.monthBbl = 0;
  }
}

export function bust(state, fieldId) {
  const h = hotState(state, fieldId);
  const fine = bustFine(state, fieldId);
  const fed = federalLaw(state);
  spend(state, fine, 'fines', false);
  state.company.reputation = clamp(state.company.reputation + HOT_OIL.reputation, 0, 100);
  const until = state.day + HOT_OIL.shutInDays;
  for (const w of Object.values(state.wells)) {
    if (w.fieldId === fieldId && w.prod) { w.prod.forcedUntil = Math.max(w.prod.forcedUntil ?? 0, until); w.prod.hot = 0; }
  }
  h.mult = 0; h.bustUntil = until; h.log = []; h.monthBbl = 0;
  state.company.stats.hotOilBusts = (state.company.stats.hotOilBusts ?? 0) + 1;
  const name = fieldDef(fieldId)?.name ?? fieldId;
  notify(state, 'hotOilBust', { fieldId, fine, federal: fed, pause: true, level: 'warn',
    text: `${fed ? 'Federal agents' : 'Railroad Commission inspectors'} caught your hot oil at ${name}: fined ${fmt$(fine)}, every well shut in for ${HOT_OIL.shutInDays} days.` });
}

// ---- actions ----------------------------------------------------------------------
// setProduction(state, wellId, { hot, mult }): hot=true produces `mult` (0-1, default 1) of the excess over the allowable.
export function setProductionImpl(state, wellId, { hot = false, mult = 1 } = {}) {
  const w = state.wells[wellId];
  if (!w?.prod) return { ok: false, msg: 'Only completed wells have a production setting.' };
  if (w.ownerOperatorId) return { ok: false, msg: `${w.name} belongs to the operator.` };
  if (!groupOf(w.fieldId)) return { ok: false, msg: `${fieldDef(w.fieldId)?.name} has no prorationing: every barrel is legal.` };
  const m = hot ? clamp(Number(mult) || 0, 0, 1) : 0;
  w.prod.hot = m;
  const h = hotState(state, w.fieldId);
  h.mult = Math.max(...wellsOnField(state, w.fieldId).filter((x) => x.prod).map((x) => x.prod.hot ?? 0), 0);
  return { ok: true, msg: m > 0 ? `${w.name} running hot: ${Math.round(m * 100)}% of the oil over the allowable goes out by night.` : `${w.name} held to its allowable.` };
}

export function setHotOilImpl(state, fieldId, on, mult = 1) {
  const f = state.fields[fieldId];
  if (!f) return { ok: false, msg: 'No such field.' };
  if (!groupOf(fieldId)) return { ok: false, msg: `${fieldDef(fieldId)?.name} has no prorationing: every barrel is legal.` };
  const m = on ? clamp(Number(mult) || 0, 0, 1) : 0;
  let n = 0;
  for (const w of wellsOnField(state, fieldId)) if (w.prod && !w.ownerOperatorId) { w.prod.hot = m; n++; }
  hotState(state, fieldId).mult = m;
  if (!n) return { ok: true, msg: 'Setting saved; no producing wells on the field yet.' };
  return { ok: true, msg: m > 0 ? `Running ${n} well${n > 1 ? 's' : ''} hot at ${fieldDef(fieldId)?.name}. Watch for inspectors.` : `All wells at ${fieldDef(fieldId)?.name} held to the allowable.` };
}

// ---- view ----------------------------------------------------------------------
export function prorationView(state, fieldId) {
  const fdef = fieldDef(fieldId);
  if (!fdef || !state.fields[fieldId]) return null;
  const group = groupOf(fieldId);
  const g = PRORATION[group] ?? null;
  const y = yf(state);
  const active = prorationActive(state, fieldId);
  const wells = wellsOnField(state, fieldId).filter((w) => w.prod).map((w) => ({
    id: w.id, name: w.name, status: w.status, potential: w.prod.potential ?? w.prod.rate, allowable: w.prod.allowable ?? null,
    rate: w.prod.rate, hot: w.prod.hot ?? 0, hotToday: w.prod.hotToday ?? 0,
  }));
  const h = state.fields[fieldId].hot;
  return {
    fieldId, group, active, law: lawText(state, fieldId),
    schedule: g ? { from: g.from, flag: g.flag, voluntary: !!g.voluntary } : null,
    allowablePerWell: g?.perWell && active ? lerpAt(g.perWell, y) : null,
    fractionOfPotential: g?.fractionOfPotential && active ? lerpAt(g.fractionOfPotential, y) : null,
    stripperBbld: STRIPPER_BBLD,
    wells,
    hot: { on: (h?.mult ?? 0) > 0, mult: h?.mult ?? 0, monthBbl: h?.monthBbl ?? 0, bbl90: hotBbl90(state, fieldId),
      bustChanceMonth: bustChance(state, fieldId), fineIfBust: bustFine(state, fieldId), federal: federalLaw(state),
      martialLaw: martialLaw(state, fieldId), bustUntil: h?.bustUntil ?? 0 },
    hotPriceMult: HOT_OIL.priceMult, hotPrice: hotPrice(state, fieldId),
    month: dayToDate(state.day).month,
  };
}
